import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFormikContext } from "formik";
import { object, string } from "yup";
import { Button, Card, Flex, Select } from "@jsq/ds/core";
import { Form, FormSelect, FormTextInput, SubmitButton } from "@jsq/ds/forms";
import {
  type DataPackageOption,
  useDataPackageSections,
} from "../hooks/useDataPackageSections";
import * as styles from "../styles_vanilla.css";
import { getTemplateNameError } from "../templateNameValidation";
import {
  useAvailableReportTypeOptions,
  REPORT_TYPE_ENUM_VALUES,
  type ReportType,
  toSupportedTemplateType,
} from "../types";
import { FormQuerySelectSection, type QueryPreviewContext } from "./FormQuerySelectSection";
export type { DataPackageOption } from "../hooks/useDataPackageSections";

export type GeneralSettingsFormValues = {
  templateName: string;
  templateType: ReportType | "";
  dataPackage: string;
  query: string;
};

const createValidationSchema = (getExistingNames: () => Set<string>) =>
  object({
    templateName: string()
      .required("Template name is required")
      .test("name-validation", "", function (value) {
        const error = getTemplateNameError(value ?? "", getExistingNames());
        return error ? this.createError({ message: error }) : true;
      }),
    templateType: string()
      .required("Template type is required")
      .oneOf([...REPORT_TYPE_ENUM_VALUES], "Select a valid template type"),
    dataPackage: string().required("Data package is required"),
    query: string().required("Query is required"),
  });

type DirtyStateWatcherProps = {
  onDirtyChange: (isDirty: boolean) => void;
};

const DirtyStateWatcher = ({ onDirtyChange }: DirtyStateWatcherProps) => {
  const { dirty } = useFormikContext();

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  return null;
};

export type GeneralSettingsStepProps = {
  initialValues: GeneralSettingsFormValues;
  existingTemplateNames: Set<string>;
  onSubmit: (values: GeneralSettingsFormValues) => void;
  onPreviewFields: (context: QueryPreviewContext) => void;
  onDirtyChange: (isDirty: boolean) => void;
  onTemplateTypeChange?: (templateType: ReportType | "") => void;
  isLoading: boolean;
  dataPackageOptions: DataPackageOption[];
  dataPackagesLoading: boolean;
  dataPackagesError?: boolean;
  viewerArenaId?: number | null;
  arenaName: string;
  onBack?: () => void;
};

type FormFooterProps = {
  isLoading: boolean;
  onBack?: () => void;
};

const FormFooter = ({ isLoading, onBack }: FormFooterProps) => {
  const { values, errors } = useFormikContext<GeneralSettingsFormValues>();
  const hasErrors = Object.keys(errors).length > 0;
  const hasEmptyRequiredField =
    !values.templateName || !values.templateType || !values.dataPackage || !values.query;

  return (
    <Flex justifyContent={onBack ? "space-between" : "flex-end"}>
      {onBack && (
        <Button variant="neutral" onClick={onBack}>
          Back
        </Button>
      )}
      <SubmitButton disabled={isLoading || hasEmptyRequiredField || hasErrors}>
        Next
      </SubmitButton>
    </Flex>
  );
};

/**
 * Watches `templateType` and:
 * 1. Resets `dataPackage` + `query` whenever it changes away from a
 *    previously-selected value.
 * 2. Notifies the parent via `onTemplateTypeChange` so it can refetch
 *    data packages with the new backend filter.
 *
 * Lives inside the Formik tree so the outer component does not need to
 * thread setFieldValue through props.
 */
const TemplateTypeResetter = ({
  onTemplateTypeChange,
}: {
  onTemplateTypeChange?: (templateType: ReportType | "") => void;
}) => {
  const { values, setFieldValue } = useFormikContext<GeneralSettingsFormValues>();
  const previousTemplateTypeRef = useRef(values.templateType);

  useEffect(() => {
    const previous = previousTemplateTypeRef.current;
    if (previous === values.templateType) return;
    previousTemplateTypeRef.current = values.templateType;
    onTemplateTypeChange?.(values.templateType);
    if (previous !== "") {
      void setFieldValue("dataPackage", "");
      void setFieldValue("query", "");
    }
  }, [values.templateType, setFieldValue, onTemplateTypeChange]);

  return null;
};

type FormBodyProps = {
  dataPackageOptions: DataPackageOption[];
  dataPackagesLoading: boolean;
  dataPackagesError?: boolean;
  arenaName: string;
  viewerArenaId?: number | null;
  onPreviewFields: (context: QueryPreviewContext) => void;
  isLoading: boolean;
  onBack?: () => void;
};

const getDataPackagePlaceholder = (
  isLoading: boolean,
  hasSections: boolean,
  templateTypeSelected: boolean,
) => {
  if (isLoading) return "Loading data packages...";
  if (!hasSections && templateTypeSelected) {
    return "No data packages available for this template type";
  }
  return "Select a data package";
};

const FormBody = ({
  dataPackageOptions,
  dataPackagesLoading,
  dataPackagesError,
  arenaName,
  viewerArenaId,
  onPreviewFields,
  isLoading,
  onBack,
}: FormBodyProps) => {
  const { values, setFieldValue } = useFormikContext<GeneralSettingsFormValues>();
  const visibleReportTypeOptions = useAvailableReportTypeOptions();
  const templateTypeSelected = values.templateType !== "";
  const supportedTemplateType = toSupportedTemplateType(values.templateType);

  // When packages finish loading, clear dataPackage + query if the currently-selected
  // package is absent from the (backend-filtered) results. This handles the edit-mode
  // case where a template was saved with a package that is incompatible with its
  // template type.
  useEffect(() => {
    if (dataPackagesLoading) return;
    if (dataPackagesError) return;
    if (!values.dataPackage) return;
    const isInOptions = dataPackageOptions.some((pkg) => String(pkg.id) === values.dataPackage);
    if (isInOptions) return;
    void setFieldValue("dataPackage", "");
    void setFieldValue("query", "");
  }, [
    dataPackagesLoading,
    dataPackagesError,
    dataPackageOptions,
    values.dataPackage,
    setFieldValue,
  ]);

  const dataPackageSections = useDataPackageSections(dataPackageOptions, arenaName);

  const hasSections = dataPackageSections.length > 0;

  return (
    <Flex flexDirection="column" gap={24}>
      <FormTextInput name="templateName" label="Template name" isFullWidth />
      <FormSelect
        name="templateType"
        label="Template type"
        placeholder="Select a template type"
        full
      >
        <Select.Section>
          {visibleReportTypeOptions.map((config) => (
            <Select.Option key={config.value} value={config.value}>
              {config.label}
            </Select.Option>
          ))}
        </Select.Section>
      </FormSelect>
      {templateTypeSelected && (
        <FormSelect
          name="dataPackage"
          label="Data package"
          placeholder={getDataPackagePlaceholder(
            dataPackagesLoading,
            hasSections,
            templateTypeSelected,
          )}
          full
          disabled={dataPackagesLoading || !hasSections}
        >
          {dataPackageSections.map((section) => (
            <Select.Section key={section.name} name={section.name}>
              {section.packages.map((pkg) => (
                <Select.Option key={pkg.id} value={String(pkg.id)}>
                  {pkg.name}
                </Select.Option>
              ))}
            </Select.Section>
          ))}
        </FormSelect>
      )}
      <FormQuerySelectSection
        dataPackageOptions={dataPackageOptions}
        viewerArenaId={viewerArenaId}
        onPreviewFields={onPreviewFields}
        supportedTemplateType={supportedTemplateType}
      />

      <FormFooter isLoading={isLoading} onBack={onBack} />
    </Flex>
  );
};

export const GeneralSettingsStep = ({
  initialValues,
  existingTemplateNames,
  onSubmit,
  onPreviewFields,
  onDirtyChange,
  onTemplateTypeChange,
  isLoading,
  dataPackageOptions,
  dataPackagesLoading,
  dataPackagesError,
  viewerArenaId,
  arenaName,
  onBack,
}: GeneralSettingsStepProps) => {
  const existingTemplateNamesRef = useRef(existingTemplateNames);
  existingTemplateNamesRef.current = existingTemplateNames;
  const validationSchema = useMemo(
    () => createValidationSchema(() => existingTemplateNamesRef.current),
    [],
  );

  const handleSubmit = useCallback(
    (values: GeneralSettingsFormValues) => {
      onSubmit(values);
    },
    [onSubmit],
  );

  return (
    <Card className={styles.formCard} title="Template settings">
      <Form<GeneralSettingsFormValues>
        initialValues={initialValues}
        enableReinitialize
        yupValidationSchema={validationSchema}
        onSubmit={handleSubmit}
        submitOnlyChangedValues={false}
      >
        <DirtyStateWatcher onDirtyChange={onDirtyChange} />
        <TemplateTypeResetter onTemplateTypeChange={onTemplateTypeChange} />
        <FormBody
          dataPackageOptions={dataPackageOptions}
          dataPackagesLoading={dataPackagesLoading}
          dataPackagesError={dataPackagesError}
          arenaName={arenaName}
          viewerArenaId={viewerArenaId}
          onPreviewFields={onPreviewFields}
          isLoading={isLoading}
          onBack={onBack}
        />
      </Form>
    </Card>
  );
};
